import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useGetAdminTaskQuery,
  useRunTaskMutation,
  useStopTaskMutation,
  useSetTaskPendingMutation,
  useDeleteTaskMutation,
  useUpdateAdminTaskMutation,
  useSubmitTask2FACodeMutation,
  useGetTask2FAStatusQuery,
  useGetBackendUserBotConnectionsQuery,
  useLazyGetTaskStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskAllLogsQuery,
  useLazyGetTaskEmployeeStatsQuery,
  useLazyGetTaskCloudWatchLogsQuery,
} from "features/adminUsers/adminUsers";
import { useRefreshTokenMutation } from "features/auth/auth";
import { useAppSelector } from "features/store";
import { taskLogsWs, TaskLogsWsMessage, TaskLogEvent } from "features/ws/taskLogsWs";

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

const RUNNING_STATUSES = ["ECS_CONTAINER_STARTED", "PROCESSING"];

function taskRunStatusChipColor(status: string): ChipColor {
  switch ((status || "").toUpperCase()) {
    case "PROCESSING":
    case "ECS_CONTAINER_STARTED":
      return "primary";
    case "FINISHED":
      return "success";
    case "PENDING":
      return "default";
    case "ERROR":
    case "CRITICAL_ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function botWorkStatusChipColor(status: string): ChipColor {
  switch ((status || "").toUpperCase()) {
    case "2FA":
      return "warning";
    case "LOGGED_IN":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: { message?: string } })?.data;
  return data?.message || fallback;
}

function formatTs(ms?: number | null): string {
  if (!ms) return "";
  try {
    return new Date(ms).toISOString().replace("T", " ").replace("Z", "");
  } catch {
    return "";
  }
}

function humanizeKind(kind?: string | null): string {
  const v = (kind || "").trim();
  if (!v) return "";
  return v
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Workspace-style label tile, matching the Major Job workspace header cards.
function StripItem({
  title,
  value,
  avatar,
  valueColor,
  onClick,
  tooltip,
}: {
  title: string;
  value: string;
  avatar?: boolean;
  valueColor?: string;
  onClick?: () => void;
  tooltip?: string;
}) {
  const tile = (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: avatar ? 1 : 0,
        px: 1.25,
        py: 0.6,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
        border: 1,
        borderColor: "divider",
        boxShadow: (theme) =>
          theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s",
        "&:hover": onClick ? { borderColor: "primary.main" } : undefined,
      }}
    >
      {avatar ? (
        <Box sx={{ mt: 0.2 }}>
          <PersonOutlineIcon color="primary" sx={{ fontSize: 21, opacity: 0.9 }} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1.1, letterSpacing: 0.06, fontWeight: 600, display: "block" }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ lineHeight: 1.35, color: valueColor, wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{tile}</Tooltip> : tile;
}

// Light, AWS-CloudWatch-like log surface. Height is controlled by the caller so
// the box can flex-fill the panel instead of forcing a fixed scroll region.
const LOG_BOX_SX = {
  fontFamily: "monospace",
  fontSize: "0.78rem",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  m: 0,
  p: 1.5,
  bgcolor: "#ffffff",
  color: "#1a1a1a",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  overflow: "auto",
};

// Fixed, generous log viewport (AWS-CloudWatch-like). The box never grows past
// this — overflow scrolls inside it instead of stretching the workspace.
const LOG_BOX_HEIGHT = { xs: 320, sm: 400, md: 460 };

// ---------------------------------------------------------------------------
// Live logs tab
// ---------------------------------------------------------------------------

// AWS-CloudWatch-style log line: gray timestamp, error lines red.
const LEADING_TS_RX =
  /^\s*\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?\s*Z?\s*(?:[-|:]?\s*)?/;
const ERROR_RX = /(?:\b|^)(ERROR|CRITICAL|Traceback|Exception)\b/i;

function stripLeadingTs(msg: string): string {
  const m = LEADING_TS_RX.exec(msg);
  if (!m) return msg;
  return msg.slice(m[0].length);
}

function LogLine({ event }: { event: TaskLogEvent }) {
  const raw = event.message ?? "";
  const text = stripLeadingTs(raw);
  const isError = ERROR_RX.test(text);
  const ts = event.timestamp ? formatTs(event.timestamp) : "";
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        py: 0.15,
        px: 0.5,
        borderRadius: 0.5,
        "&:hover": { bgcolor: "#f5f5f5" },
        fontFamily: "monospace",
        fontSize: "0.78rem",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: isError ? "#d32f2f" : "#1a1a1a",
      }}
    >
      {ts ? (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            color: "#5f6b7a",
            fontSize: "0.74rem",
          }}
        >
          {ts}
        </Box>
      ) : null}
      <Box component="span" sx={{ flex: 1 }}>
        {text}
      </Box>
    </Box>
  );
}

const RANGE_PRESETS: { key: string; label: string; ms: number | null }[] = [
  { key: "live", label: "Live tail (latest)", ms: null },
  { key: "1m", label: "Last 1 minute", ms: 60_000 },
  { key: "5m", label: "Last 5 minutes", ms: 5 * 60_000 },
  { key: "15m", label: "Last 15 minutes", ms: 15 * 60_000 },
  { key: "30m", label: "Last 30 minutes", ms: 30 * 60_000 },
  { key: "1h", label: "Last 1 hour", ms: 60 * 60_000 },
  { key: "3h", label: "Last 3 hours", ms: 3 * 60 * 60_000 },
  { key: "12h", label: "Last 12 hours", ms: 12 * 60 * 60_000 },
  { key: "1d", label: "Last 24 hours", ms: 24 * 60 * 60_000 },
  { key: "all", label: "All available", ms: -1 },
  { key: "custom", label: "Custom range…", ms: 0 },
];

const CLEAR_LS_PREFIX = "taskLogs.cleared.";
const clearKey = (id: number) => `${CLEAR_LS_PREFIX}${id}`;

function readClearedAt(taskId: number): number | null {
  try {
    const raw = localStorage.getItem(clearKey(taskId));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function LiveLogsTab({
  taskId,
  ecsTaskId,
  liveAvailable,
  taskTerminal,
}: {
  taskId: number;
  ecsTaskId: string;
  liveAvailable: boolean;
  isLocalRun: boolean;
  taskTerminal: boolean;
}) {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [refreshToken] = useRefreshTokenMutation();
  const [fetchCw] = useLazyGetTaskCloudWatchLogsQuery();

  // mode: live = WS streaming; history = static range view; cleared = banner only
  const [mode, setMode] = useState<"live" | "history" | "cleared">(() =>
    readClearedAt(taskId) ? "cleared" : "live",
  );
  const [clearedAt, setClearedAt] = useState<number | null>(() => readClearedAt(taskId));

  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [liveEvents, setLiveEvents] = useState<TaskLogEvent[]>([]);
  // Bumped by the manual Refresh button to force a WS reconnect (re-runs the effect).
  const [liveNonce, setLiveNonce] = useState(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const pendingBufRef = useRef<TaskLogEvent[]>([]);
  const liveBoxRef = useRef<HTMLDivElement | null>(null);

  // History view state
  const [historyEvents, setHistoryEvents] = useState<TaskLogEvent[]>([]);
  const [historyRange, setHistoryRange] = useState<{ start: number; end: number; label: string } | null>(null);
  const [historyNextToken, setHistoryNextToken] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [presetKey, setPresetKey] = useState<string>("live");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");

  const dropPending = () => {
    pendingBufRef.current = [];
    setPendingCount(0);
  };

  // WebSocket live tail — only when mode === "live"
  useEffect(() => {
    if (!liveAvailable || mode !== "live") return;
    taskLogsWs.setTokenProvider(() => localStorage.getItem("accessToken"));
    const unStatus = taskLogsWs.subscribeStatus(setConnected);
    const unAuth = taskLogsWs.subscribeAuthRejected(() => {
      refreshToken()
        .unwrap()
        .then((result) => {
          if (result?.access) taskLogsWs.connect(result.access, taskId);
        })
        .catch(() => undefined);
    });
    const unMsg = taskLogsWs.subscribe((msg: TaskLogsWsMessage) => {
      if (msg.type === "logs") {
        const incoming = msg.events;
        if (pausedRef.current) {
          // Buffer while paused so Resume reveals everything (no data loss).
          const buf = pendingBufRef.current.concat(incoming);
          pendingBufRef.current = buf.length > 5000 ? buf.slice(buf.length - 5000) : buf;
          setPendingCount(pendingBufRef.current.length);
          return;
        }
        setLiveEvents((prev) => {
          const next = prev.concat(incoming);
          return next.length > 5000 ? next.slice(next.length - 5000) : next;
        });
      } else if (msg.type === "end") {
        setEnded(msg.status || "finished");
      } else if (msg.type === "unavailable") {
        setUnavailable(true);
      }
    });

    const token = accessToken || localStorage.getItem("accessToken");
    if (token) taskLogsWs.connect(token, taskId);

    return () => {
      unStatus();
      unAuth();
      unMsg();
      taskLogsWs.disconnect();
      taskLogsWs.setTokenProvider(null);
    };
  }, [taskId, ecsTaskId, liveAvailable, mode, accessToken, refreshToken, liveNonce]);

  // A re-run assigns a fresh ECS task id — drop the previous run's buffered lines
  // so the live viewport reflects the new run without a manual page refresh.
  const prevEcsRef = useRef(ecsTaskId);
  useEffect(() => {
    if (prevEcsRef.current === ecsTaskId) return;
    prevEcsRef.current = ecsTaskId;
    setLiveEvents([]);
    pendingBufRef.current = [];
    setPendingCount(0);
    setEnded(null);
    setUnavailable(false);
  }, [ecsTaskId]);

  // Auto-scroll live box
  useEffect(() => {
    if (paused || mode !== "live") return;
    const el = liveBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveEvents, paused, mode]);

  const runHistoryQuery = async (start: number, end: number, label: string, append = false) => {
    setHistoryLoading(true);
    try {
      const res = await fetchCw({
        taskId,
        start,
        end,
        nextToken: append ? historyNextToken ?? undefined : undefined,
      }).unwrap();
      const events = res.events ?? [];
      setHistoryEvents((prev) => (append ? prev.concat(events) : events));
      setHistoryNextToken(res.next_token ?? null);
      if (!append) {
        setHistoryRange({ start, end, label });
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const switchToLive = () => {
    setMode("live");
    setHistoryEvents([]);
    setHistoryRange(null);
    setHistoryNextToken(null);
    setPresetKey("live");
    if (clearedAt != null) {
      try {
        localStorage.removeItem(clearKey(taskId));
      } catch {
        /* noop */
      }
      setClearedAt(null);
    }
  };

  const applyPreset = async (key: string) => {
    setPresetKey(key);
    if (key === "live") {
      switchToLive();
      return;
    }
    if (key === "custom") {
      // wait for Apply
      return;
    }
    const preset = RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const end = Date.now();
    let start: number;
    let label: string;
    if (preset.ms === -1) {
      // "All available" — ask backend for stream range first
      try {
        const probe = await fetchCw({ taskId }).unwrap();
        if (probe.local || !probe.range) {
          setHistoryEvents([]);
          setHistoryRange({ start: 0, end, label: "All available" });
          setMode("history");
          if (clearedAt != null) {
            try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
            setClearedAt(null);
          }
          return;
        }
        start = probe.range.first ?? end - 24 * 60 * 60_000;
        label = "All available";
      } catch {
        start = end - 24 * 60 * 60_000;
        label = "All available";
      }
    } else {
      start = end - (preset.ms ?? 0);
      label = preset.label;
    }
    setMode("history");
    if (clearedAt != null) {
      try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
      setClearedAt(null);
    }
    await runHistoryQuery(start, end, label, false);
  };

  const applyCustomRange = async () => {
    const s = new Date(customStart).getTime();
    const e = new Date(customEnd).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || s >= e) return;
    setMode("history");
    if (clearedAt != null) {
      try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
      setClearedAt(null);
    }
    await runHistoryQuery(s, e, `${formatTs(s)} → ${formatTs(e)}`, false);
  };

  const onClearLogs = () => {
    setLiveEvents([]);
    dropPending();
    setPaused(false);
    const now = Date.now();
    try {
      localStorage.setItem(clearKey(taskId), String(now));
    } catch {
      /* noop */
    }
    setClearedAt(now);
    setMode("cleared");
    taskLogsWs.disconnect();
  };

  const onResume = () => {
    if (pendingBufRef.current.length) {
      setLiveEvents((prev) => {
        const next = prev.concat(pendingBufRef.current);
        return next.length > 5000 ? next.slice(next.length - 5000) : next;
      });
      dropPending();
    }
    setPaused(false);
  };

  // Force-reconnect the live tail: reset transient flags and bump the nonce so the
  // WS effect tears down and reconnects.
  const refreshLive = () => {
    setEnded(null);
    setUnavailable(false);
    setPaused(false);
    dropPending();
    setLiveNonce((n) => n + 1);
  };

  // Reset transient state when switching tasks
  useEffect(() => {
    setLiveEvents([]);
    dropPending();
    setHistoryEvents([]);
    setHistoryRange(null);
    setHistoryNextToken(null);
    setEnded(null);
    setUnavailable(false);
    setPaused(false);
    const cleared = readClearedAt(taskId);
    setClearedAt(cleared);
    setMode(cleared ? "cleared" : "live");
    setPresetKey("live");
  }, [taskId]);

  if (!liveAvailable || unavailable) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Live logs are not available yet. Status/file logs and task controls remain available.
      </Alert>
    );
  }

  const showLiveViewport = mode === "live";
  const showHistoryViewport = mode === "history";
  const showClearedBanner = mode === "cleared";

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2">Logs</Typography>
          {mode === "live" ? (
            <Chip
              size="small"
              label={connected ? "Live" : "Connecting…"}
              color={connected ? "success" : "default"}
              variant="outlined"
            />
          ) : (
            <Chip size="small" label={mode === "history" ? "History" : "Cleared"} variant="outlined" />
          )}
          {ended && (
            <Chip size="small" label={`Ended: ${ended}`} color="info" variant="outlined" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            select
            size="small"
            value={presetKey}
            onChange={(e) => void applyPreset(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {RANGE_PRESETS.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {mode === "live" && (
            <>
              <Button size="small" startIcon={<RefreshIcon />} onClick={refreshLive}>
                Refresh
              </Button>
              <Button
                size="small"
                onClick={() => (paused ? onResume() : setPaused(true))}
                color={paused && pendingCount > 0 ? "warning" : "primary"}
              >
                {paused ? `Resume${pendingCount ? ` (${pendingCount})` : ""}` : "Pause"}
              </Button>
              <Button size="small" onClick={onClearLogs}>
                Clear
              </Button>
            </>
          )}
          {mode === "history" && (
            <Button size="small" onClick={switchToLive}>
              Back to live
            </Button>
          )}
        </Stack>

        {presetKey === "custom" && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              size="small"
              type="datetime-local"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
            <Button size="small" variant="outlined" onClick={() => void applyCustomRange()}>
              Apply
            </Button>
          </Stack>
        )}

        {taskTerminal && mode === "live" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This task has finished — the live tail won&apos;t update. Use the dropdown above to browse a time range.
          </Typography>
        )}

        {showLiveViewport && (
          <Box
            ref={liveBoxRef}
            sx={{ ...LOG_BOX_SX, height: LOG_BOX_HEIGHT, display: "block" }}
          >
            {liveEvents.length ? (
              liveEvents.map((ev, idx) => <LogLine key={idx} event={ev} />)
            ) : (
              <Typography variant="body2" sx={{ color: "#5f6b7a", fontFamily: "monospace" }}>
                {taskTerminal
                  ? "(task finished — pick a time range above to view stored logs)"
                  : "(waiting for new lines…)"}
              </Typography>
            )}
          </Box>
        )}

        {showHistoryViewport && (
          <Box>
            {historyRange && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Showing: {historyRange.label}
              </Typography>
            )}
            <Box sx={{ ...LOG_BOX_SX, height: LOG_BOX_HEIGHT, display: "block" }}>
              {historyLoading && historyEvents.length === 0 ? (
                <CircularProgress size={18} sx={{ m: 1 }} />
              ) : historyEvents.length ? (
                historyEvents.map((ev, idx) => <LogLine key={idx} event={ev} />)
              ) : (
                <Typography variant="body2" sx={{ color: "#5f6b7a", fontFamily: "monospace" }}>
                  (no records in this range)
                </Typography>
              )}
            </Box>
            {historyNextToken && historyRange && (
              <Button
                size="small"
                disabled={historyLoading}
                onClick={() =>
                  void runHistoryQuery(historyRange.start, historyRange.end, historyRange.label, true)
                }
                sx={{ mt: 0.5 }}
              >
                Load more
              </Button>
            )}
          </Box>
        )}

        {showClearedBanner && (
          <Alert
            severity="info"
            sx={{ mt: 1 }}
            action={
              <Button size="small" onClick={switchToLive}>
                Resume live
              </Button>
            }
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Logs were cleared{clearedAt ? ` on ${new Date(clearedAt).toLocaleString()}` : ""}. Pick a range to reload, or load everything.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              {["5m", "15m", "1h", "3h", "all"].map((k) => {
                const p = RANGE_PRESETS.find((x) => x.key === k);
                if (!p) return null;
                return (
                  <Button
                    key={k}
                    size="small"
                    variant="outlined"
                    onClick={() => void applyPreset(k)}
                  >
                    {k === "all" ? "Load all logs" : `Last ${k}`}
                  </Button>
                );
              })}
            </Stack>
          </Alert>
        )}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Static (status / file) logs tab
// ---------------------------------------------------------------------------

function StaticLogsTab({
  kind,
  taskId,
}: {
  kind: "status" | "file";
  taskId: number;
}) {
  const [fetchStatus] = useLazyGetTaskStatusLogsQuery();
  const [fetchFile] = useLazyGetTaskFileLogsQuery();
  const [logs, setLogs] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res =
        kind === "file"
          ? await fetchFile(taskId).unwrap()
          : await fetchStatus(taskId).unwrap();
      setLogs(res.logs);
    } catch {
      setLogs("(failed to load logs)");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [taskId, kind]);

  const hasContent = !loading && !!logs && logs !== "(no records)" && logs !== "(failed to load logs)";

  const download = () => {
    const blob = new Blob([logs], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}_logs_task_${taskId}.log`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2">
          {kind === "file" ? "File logs" : "Status logs"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} disabled={!hasContent} onClick={download}>
          Download
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} disabled={loading} onClick={load}>
          Refresh
        </Button>
      </Stack>
      <Box
        component="pre"
        sx={{
          ...LOG_BOX_SX,
          height: LOG_BOX_HEIGHT,
          ...(loading
            ? { display: "flex", alignItems: "center", justifyContent: "center" }
            : {}),
        }}
      >
        {loading ? <CircularProgress size={22} /> : logs || "(no records)"}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Task workspace (rendered inside Bots Management, in place of Major job workspace)
// ---------------------------------------------------------------------------

export type TaskWorkspaceProps = {
  taskId: number;
  userId: number;
  /** Return to the job / major-job view. */
  onBack: () => void;
  /** Jump to the connections screen for this user. */
  onManageConnections: () => void;
};

function TaskWorkspace({ taskId, userId, onBack, onManageConnections }: TaskWorkspaceProps) {
  const canEditBots = Boolean(
    useAppSelector((s) => s.auth.user?.platform_profile?.is_superuser),
  );
  // Poll only while the task is not terminal, so a PENDING → PROCESSING → 2FA
  // transition surfaces without a manual refresh even when the WS push is mute
  // (connected-but-no-Redis on Dev). Terminal tasks don't poll — the refresh
  // button no longer spins on its own once a task has finished.
  const [taskPollInterval, setTaskPollInterval] = useState(4000);
  const { data: task, isFetching, refetch } = useGetAdminTaskQuery(taskId, {
    skip: !taskId,
    pollingInterval: taskPollInterval,
  });


  const [runTask] = useRunTaskMutation();
  const [stopTask] = useStopTaskMutation();
  const [setTaskPending] = useSetTaskPendingMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [updateTask] = useUpdateAdminTaskMutation();
  const [submit2FA] = useSubmitTask2FACodeMutation();

  const [tab, setTab] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  // Spinner for the manual Refresh button only. `isFetching` also flips on every
  // background poll, which made the button spin on its own — track explicit clicks.
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaManualOverride, setTwoFaManualOverride] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [connOpen, setConnOpen] = useState(false);
  const [connSelected, setConnSelected] = useState<number | "">("");

  const { data: connData } = useGetBackendUserBotConnectionsQuery(userId, {
    skip: !userId || !connOpen,
  });

  const taskStatus = (task?.task_status as string) || "";
  const botStatus = (task?.bot_work_status as string) || "";
  const liveAvailable = Boolean(task?.live_logs_available);
  const isLocalRun = Boolean(task?.is_local_run);
  const isSsnTask = String(task?.bot_name || "").startsWith("ssn_bots.");
  const isRunning = RUNNING_STATUSES.includes(taskStatus.toUpperCase());
  const taskTerminal = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"].includes(
    taskStatus.toUpperCase(),
  );

  useEffect(() => {
    setTaskPollInterval(taskTerminal ? 0 : 4000);
  }, [taskTerminal]);

  // 2FA visibility mirrors the Major Job workspace: bot is waiting on 2FA AND a
  // pending/ready 2FA request exists. Poll while running so it surfaces even if
  // the WS push is unavailable.
  const { data: twoFaData, refetch: refetch2FA } = useGetTask2FAStatusQuery(taskId, {
    skip: !taskId,
    pollingInterval: isRunning ? 3000 : 0,
  });
  const twoFaStatus = (twoFaData?.status || "").toLowerCase();
  const show2FA =
    botStatus.toUpperCase() === "2FA" && ["pending", "ready"].includes(twoFaStatus);
  // While the bot fetches the code automatically (auto_until in the future) the
  // manual input stays hidden; the button below lets the operator force it early.
  const twoFaAutoUntilMs = twoFaData?.auto_until ? Date.parse(twoFaData.auto_until) : NaN;
  const twoFaAutoActive =
    twoFaStatus === "pending" &&
    Number.isFinite(twoFaAutoUntilMs) &&
    Date.now() < twoFaAutoUntilMs &&
    !twoFaManualOverride;

  // Non-terminal tasks poll via `taskPollInterval` above (WS is unreliable on Dev);
  // finished tasks refresh only via WebSocket or the manual Refresh button.

  // Close the 2FA dialog as soon as a code is no longer needed.
  useEffect(() => {
    if (!show2FA && twoFaOpen) setTwoFaOpen(false);
  }, [show2FA, twoFaOpen]);

  // A fresh 2FA request starts a fresh auto phase.
  useEffect(() => {
    if (!show2FA && twoFaManualOverride) setTwoFaManualOverride(false);
  }, [show2FA, twoFaManualOverride]);

  // Success/error counts computed from the task's status-log file (falls back to
  // live DB counters server-side when no log zip exists yet).
  const [fetchEmployeeStats] = useLazyGetTaskEmployeeStatsQuery();
  const [empStats, setEmpStats] = useState<{ success: number; error: number } | null>(
    null,
  );
  const reloadStats = () => {
    if (!taskId || isSsnTask) return;
    fetchEmployeeStats(taskId)
      .unwrap()
      .then((r) => setEmpStats({ success: r.success, error: r.error }))
      .catch(() => undefined);
  };
  // Refresh the displayed Success/Errors on the same cadence as the task poll so the
  // numbers update on their own. Fetch immediately on mount and on the terminal flip
  // (final counts), poll every 4s only while the task is still running.
  useEffect(() => {
    if (!taskId || isSsnTask) return;
    const load = () =>
      fetchEmployeeStats(taskId)
        .unwrap()
        .then((r) => setEmpStats({ success: r.success, error: r.error }))
        .catch(() => undefined);
    load();
    if (taskTerminal) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [taskId, isSsnTask, taskTerminal, fetchEmployeeStats]);

  const run = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      if (taskStatus.toUpperCase() !== "PENDING") await setTaskPending(taskId).unwrap();
      await runTask(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to run task."));
    }
  };

  const stop = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      await stopTask(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to stop task."));
    }
  };

  const doDelete = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      await deleteTask(taskId).unwrap();
      setDeleteOpen(false);
      onBack();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to delete task."));
      setDeleteOpen(false);
    }
  };

  const doSubmit2FA = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      await submit2FA({ taskId, code: twoFaCode.trim() }).unwrap();
      setTwoFaOpen(false);
      setTwoFaCode("");
      // Poll until the bot consumes the code and the prompt clears.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const [, res] = await Promise.all([refetch(), refetch2FA()]);
        const fresh = (res.data?.status || "").toLowerCase();
        if (fresh !== "pending" && fresh !== "ready") break;
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to submit 2FA code."));
    }
  };

  const doChangeConnection = async () => {
    if (!canEditBots) return;
    if (connSelected === "") return;
    setActionError(null);
    try {
      await updateTask({ taskId, body: { connection: connSelected } }).unwrap();
      setConnOpen(false);
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to change connection."));
    }
  };

  const connections = connData?.connections ?? [];

  // --- Header labels (from TaskDetailSerializer) ---
  const userLabel = (task?.user_label as string) || "";
  const vendorLabel = (task?.connection_vendor as string) || "";

  // Connection pool for the picker: only logins of the same vendor as this task,
  // matching how the Major job workspace scopes its connection dropdown.
  const connPool = useMemo(() => {
    const v = vendorLabel.trim().toLowerCase();
    if (!v) return connections;
    return connections.filter(
      (c) => (c.vendor ?? "").trim().toLowerCase() === v,
    );
  }, [connections, vendorLabel]);
  const kindLabel = humanizeKind(task?.major_job_kind as string | undefined);
  const majorJobName = (task?.major_job_name as string) || "";
  const jobName = (task?.job_name as string) || "";
  const connUsername = (task?.connection_username as string) || "";
  const taskIndex = task?.task_index_in_job as number | null | undefined;
  const taskCount = task?.task_count_in_job as number | null | undefined;

  // Sequential task number within its job (fallback to DB id when not in a job).
  const taskTitle = taskIndex != null ? `Task ${taskIndex}` : `Task #${taskId}`;
  void taskCount;
  void userId;

  const goToConnections = onManageConnections;
  const ecsTaskId = (task?.ecs_task_id as string) || "";

  const [ecsCopied, setEcsCopied] = useState(false);
  const copyEcsTaskId = async () => {
    if (!ecsTaskId) return;
    try {
      await navigator.clipboard.writeText(ecsTaskId);
      setEcsCopied(true);
      window.setTimeout(() => setEcsCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Full task log history download (all_logs file). CloudWatch live logs only
  // cover one ECS task id and are lost on re-run; the all_logs file is the
  // complete history for this task.
  const [fetchAllLogs] = useLazyGetTaskAllLogsQuery();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const downloadAllLogs = async () => {
    setActionError(null);
    setDownloadingAll(true);
    try {
      const res = await fetchAllLogs(taskId).unwrap();
      if (!res.found || !res.logs) {
        setActionError("No all-logs file found for this task yet.");
        return;
      }
      const blob = new Blob([res.logs], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `all_logs_task_${taskId}.log`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setActionError("Failed to download all-logs file.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const infoItems = useMemo(() => {
    const items: [string, string][] = [
      ["Bot", (task?.bot_name as string) || "—"],
    ];
    if (!isSsnTask) {
      items.push([
        "Employee range",
        task ? `${task.employee_number_from} — ${task.employee_number_to}` : "—",
      ]);
      items.push([
        "Success",
        String(empStats?.success ?? task?.successfully_processed_employees_count ?? 0),
      ]);
      items.push([
        "Errors",
        String(empStats?.error ?? task?.error_employees_count ?? 0),
      ]);
    }
    return items;
  }, [task, empStats, isSsnTask]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Task workspace
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={onBack}
          sx={{ width: { xs: "100%", sm: "auto" }, minHeight: { xs: 40, sm: 0 } }}
        >
          Back to job
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {taskTitle}
              </Typography>
              {/* Mobile: even 2-column grid of meta pills; desktop: inline wrap. */}
              <Box
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "none" },
                  flexWrap: { sm: "wrap" },
                  gap: 1,
                  alignItems: { sm: "flex-start" },
                  mt: 0.75,
                }}
              >
                {userLabel && <StripItem title="User" value={userLabel} avatar />}
                {kindLabel && <StripItem title="Action" value={kindLabel} />}
                {vendorLabel && <StripItem title="Vendor" value={vendorLabel} />}
                {majorJobName && <StripItem title="Major job" value={majorJobName} />}
                {jobName && <StripItem title="Task job" value={jobName} />}
              </Box>
            </Box>
            <Tooltip title="Refresh task stats">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={manualRefreshing}
                  onClick={async () => {
                    setManualRefreshing(true);
                    try {
                      await Promise.all([refetch(), refetch2FA()]);
                      reloadStats();
                    } finally {
                      setManualRefreshing(false);
                    }
                  }}
                >
                  {manualRefreshing ? (
                    <CircularProgress size={18} color="primary" />
                  ) : (
                    <ReplayIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Status: ${taskStatus || "—"}`}
              color={taskRunStatusChipColor(taskStatus)}
            />
            <Chip
              size="small"
              label={`Bot: ${botStatus || "—"}`}
              color={botWorkStatusChipColor(botStatus)}
            />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
              mb: 1.25,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
              >
                Connection
              </Typography>
              <Tooltip title="Manage connections">
                <Typography
                  variant="body2"
                  onClick={goToConnections}
                  sx={{
                    mt: 0.25,
                    wordBreak: "break-all",
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {connUsername ||
                    (task?.connection != null ? `#${task.connection}` : "—")}
                </Typography>
              </Tooltip>
            </Box>
            {infoItems.map(([label, value]) => (
              <Box key={label}>
                <Typography
                  variant="overline"
                  color="text.primary"
                  sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
                >
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all", mt: 0.25 }}>
                  {value}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
              >
                ECS task id
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {ecsTaskId || "—"}
                </Typography>
                <Tooltip title={ecsCopied ? "Copied" : "Copy ECS task id"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={copyEcsTaskId}
                      disabled={!ecsTaskId}
                      aria-label="Copy ECS task id"
                    >
                      {ecsCopied ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.25 }} />

          {/* Mobile: full-width stacked buttons (drawer-like); desktop: inline row. */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              "& > span": { width: { xs: "100%", sm: "auto" } },
              "& .MuiButton-root": {
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: 40, sm: 0 },
              },
            }}
          >
          {isRunning ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              disabled={!canEditBots}
              onClick={stop}
            >
              Stop
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!canEditBots}
              onClick={run}
            >
              Run
            </Button>
          )}
          {show2FA && twoFaAutoActive && (
            <Tooltip title="The bot is entering the 2FA code automatically. Click to enter it manually.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<CircularProgress size={14} color="warning" />}
                  disabled={!canEditBots}
                  onClick={() => setTwoFaManualOverride(true)}
                >
                  Auto 2FA...
                </Button>
              </span>
            </Tooltip>
          )}
          {show2FA && !twoFaAutoActive && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<VpnKeyIcon />}
              disabled={!canEditBots}
              onClick={() => setTwoFaOpen(true)}
            >
              Pass 2FA
            </Button>
          )}
          <Tooltip title={isRunning ? "Stop the task first to change its connection" : ""}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                disabled={isRunning || !canEditBots}
                onClick={() => {
                  setConnSelected((task?.connection as number) ?? "");
                  setConnOpen(true);
                }}
              >
                Change connection
              </Button>
            </span>
          </Tooltip>
          {!isSsnTask && (
            <Tooltip title="Download the task's complete log history (all_logs file)">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    downloadingAll ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={downloadingAll}
                  onClick={downloadAllLogs}
                >
                  Download all logs
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isRunning || !canEditBots}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Live logs" />
          {!isSsnTask && <Tab label="Status logs" />}
          {!isSsnTask && <Tab label="File logs" />}
        </Tabs>
        {tab === 0 && (
          <LiveLogsTab
            taskId={taskId}
            ecsTaskId={ecsTaskId}
            liveAvailable={liveAvailable}
            isLocalRun={isLocalRun}
            taskTerminal={taskTerminal}
          />
        )}
        {!isSsnTask && tab === 1 && <StaticLogsTab kind="status" taskId={taskId} />}
        {!isSsnTask && tab === 2 && <StaticLogsTab kind="file" taskId={taskId} />}
      </Paper>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete {taskTitle.toLowerCase()}?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" disabled={!canEditBots} onClick={doDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA dialog */}
      <Dialog open={twoFaOpen} onClose={() => setTwoFaOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enter 2FA code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="2FA code"
            value={twoFaCode}
            onChange={(e) => setTwoFaCode(e.target.value)}
            onKeyDown={(ev) => {
              if (
                ev.key === "Enter" &&
                !ev.shiftKey &&
                twoFaCode.trim().length > 0 &&
                canEditBots
              ) {
                ev.preventDefault();
                void doSubmit2FA();
              }
            }}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFaOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!twoFaCode.trim() || !canEditBots}
            onClick={doSubmit2FA}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change connection dialog — same login labelling as the Major job workspace. */}
      <Dialog open={connOpen} onClose={() => setConnOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Change connection</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            label="Connection"
            margin="dense"
            value={connSelected}
            onChange={(e) => setConnSelected(Number(e.target.value))}
            sx={{
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            {connPool.map((c, ix) => (
              <MenuItem key={c.id} value={c.id}>
                {`${ix + 1}. ${c.username ? `@${c.username}` : "-"}`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={connSelected === "" || !canEditBots}
            onClick={doChangeConnection}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}

export default TaskWorkspace;
